//-- Copyright (c) 2021 TMT Insights Group, LLC
import PolarisIcon from '@/components/_common/PolarisIcon';
import { IngestFileHistory, UserProfile } from '@/redux/slices/slice_types';
import { TimelineConnector, TimelineContent, TimelineDot, TimelineDotProps, TimelineItem, TimelineSeparator } from '@mui/lab';
import { Box, Chip, Fade, darken, lighten } from '@mui/material';
import { CSSProperties, ReactNode } from 'react';
import UnassignedUserDisplay from '../AssigneeSelect/UnassignedUserDisplay';
import UserDisplay from '../AssigneeSelect/UserDisplay';
import DateTimeDisplay from '../DateTimeDisplay';
import TableStatus from '../Table/TableStatus';
import TimelineTimestamp from '../TimelineTimestamp';
import { EnabledHistoryTable } from './History';
import styled from '@emotion/styled';
import ListOverflowBadge from '../ListOverflowBadge/ListOverflowBadge';
import { insertWordBreaks } from '@/_utils/utils';

type HistoryTimelineItemProps = {
  item: IngestFileHistory;
  type: EnabledHistoryTable['type'];
  index: number;
};
const HistoryTimelineItem = ({ item, type, index }: HistoryTimelineItemProps) => {
  const { history_date, changes, history_user } = item;
  const hasNote = changes?.status_reason?.new || changes?.resolution_notes?.new;

  return (
    <Fade in timeout={(index + 1) * 600}>
      <StyledTimelineItem>
        <TimelineTimestamp datetime={history_date} />
        <StyledTimelineSeparator>
          <TimelineIcon changes={changes} />
          <TimelineConnector sx={{ minHeight: hasNote ? 120 : 60 }} />
        </StyledTimelineSeparator>
        <TimelineContent>
          {/* only display on mobile */}
          <Time singleLine>{history_date}</Time>
          <HistoryItem history_user={history_user} changes={changes} type={type} />
          <HistoryNote changes={changes} />
        </TimelineContent>
      </StyledTimelineItem>
    </Fade>
  );
};

export default HistoryTimelineItem;

const TimelineIcon = ({ changes }: Pick<IngestFileHistory, 'changes'>) => {
  // default is robot icon
  const defaultIcon = {
    icon: 'robot',
    height: 12,
    style: { marginTop: -1 }
  };

  let timelineIcon: {
    icon: string;
    height: number;
    style?: CSSProperties;
    color?: TimelineDotProps['color'];
    variant?: TimelineDotProps['variant'];
  } = defaultIcon;

  if (changes.hid) {
    // blue star icon
    timelineIcon = {
      icon: 'star_filled',
      height: 15,
      style: { color: 'var(--primary-main)', marginTop: -1 },
      color: 'primary'
    };
  } else if (changes.assigned_user) {
    // user icon
    timelineIcon = {
      icon: 'user_filled',
      height: 15,
      color: 'primary',
      variant: 'filled'
    };
  } else if (changes.priority) {
    // bolt icon
    timelineIcon = {
      icon: 'priority',
      height: 15,
      style: { color: 'var(--secondary-light)', marginTop: -1 },
      color: 'secondary'
    };
  }

  return (
    <>
      <TimelineDot color={timelineIcon.color || 'grey'} variant={timelineIcon.variant || 'outlined'}>
        <PolarisIcon icon={timelineIcon.icon} height={timelineIcon.height} style={timelineIcon.style} />
      </TimelineDot>
    </>
  );
};

const HistoryItem = ({
  history_user,
  type,
  changes
}: Pick<IngestFileHistory, 'history_user' | 'changes'> & { type: HistoryTimelineItemProps['type'] }) => {
  // default content is an "unidentified change" message
  let content = <StatusWord>Unidentified change</StatusWord>;

  const UserInfo = ({ user }: { user: Partial<UserProfile> }) =>
    user && <Chip label={<UserDisplay user={user} emptyText="System change" iconSize={15} />} color="info" />;

  if (changes.status) {
    // content to display if the status has changed
    content = (
      <>
        <StatusWord noMargin={+!!history_user}>{history_user ? 'changed status' : 'Status changed'}</StatusWord>
        {changes.status?.old !== 'nothing' ? ' from ' : ' '}
        {changes.status?.old !== 'nothing' ? (
          <Chip label={<TableStatus style={{ lineHeight: 1 }} status={changes.status.old} />} color="default" />
        ) : (
          ' '
        )}
        to
        <Chip label={<TableStatus style={{ lineHeight: 1 }} status={changes.status.new} />} color="default" />
      </>
    );
  } else if (changes.assigned_user) {
    // content to display if the assigned user has changed
    content = (
      <>
        <UpdateWord>{changes.assigned_user.old ? 'reassigned' : 'assigned'}</UpdateWord> {type} to{' '}
        {changes.assigned_user.new ? <UserInfo user={changes.assigned_user.new} /> : <Chip label={<UnassignedUserDisplay />} color="info" />}
      </>
    );
  } else if (changes.priority) {
    // content to display if the priority has changed
    content = (
      <>
        <UpdateWord>updated</UpdateWord> priority from
        {changes.priority.old ? <PriorityChip color="warning" label={changes.priority.old} /> : <Chip label="none" sx={{ opacity: 0.5 }} />}
        to {changes.priority.new ? <PriorityChip color="warning" label={changes.priority.new} /> : <Chip label="none" sx={{ opacity: 0.5 }} />}
      </>
    );
  } else if (changes.hid) {
    // content to display if the HID has changed (ie has been created)
    content = (
      <>
        <StatusWord noMargin={+!!history_user}>{history_user ? 'created' : 'Created'}</StatusWord>
        {type}
      </>
    );
  } else if (Object.keys(changes).length > 0) {
    const otherKeysUpdated = Object.keys(changes);

    // if data is updated, we want to display the keys of the data object instead of just 'data'
    if (otherKeysUpdated.includes('data')) {
      const dataKeys = Object.keys(changes?.data?.new);
      otherKeysUpdated.splice(otherKeysUpdated.indexOf('data'), 1, ...dataKeys);
    }

    const excludedKeys = ['data', 'id', 'updated_by', 'created_by', 'workflow_type'];
    const filteredKeys = otherKeysUpdated.filter((key) => !excludedKeys.includes(key));

    if (filteredKeys.length > 0) {
      content = (
        <Content>
          <span>updated</span>
          <ListOverflowBadge
            list={filteredKeys.map((key) => (
              <span style={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>{key}</span>
            ))}
            maxWidth={350}
            tooltipDisplay={(item: any, index: number, total: number) => {
              if (index === total - 2) return item.props.children + ', and ';
              else if (index < total - 1) return item.props.children + ', ';
              else return item.props.children;
            }}
            delineated
          />
        </Content>
      );
    }
  }

  return (
    <>
      <HistoryItemWrapper>
        <UserInfo user={history_user} />
        {content}
      </HistoryItemWrapper>
      {!!changes.source_file_path && (
        <Caption>
          <strong>Source File:</strong> {changes.source_file_path.new}
        </Caption>
      )}
    </>
  );
};

const HistoryNote = ({ changes }: Pick<IngestFileHistory, 'changes'>) => {
  const content: ReactNode[] = [];

  const entries = [
    {
      label: 'Status Detail',
      note: changes?.status_reason?.new
    },
    {
      label: 'Resolution Note',
      note: changes?.resolution_notes?.new
    }
  ];

  const NoteTemplate = ({ label, note }: (typeof entries)[0]) => (
    <NoteWrapper>
      <Note>
        <NoteLabel>{label}: </NoteLabel>
        {insertWordBreaks(note)}
      </Note>
    </NoteWrapper>
  );

  entries.forEach((entry) => {
    if (entry.note) content.push(<NoteTemplate key={entry.label} {...entry} />);
  });

  return <>{content}</>;
};

const Time = styled(DateTimeDisplay)`
  padding-top: ${({ theme }) => theme.spacing(1.25)};
  font-size: 85%;
  opacity: 0.5;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    display: none;
  }
`;

const StyledTimelineItem = styled(TimelineItem)`
  min-height: auto;
  display: flex;
  flex-direction: row;

  & .MuiTimelineDot-root {
    min-height: 28px;
    min-width: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-width: 1px;

    & > svg {
      opacity: 0.8;
    }
  }

  &:last-child .MuiTimelineConnector-root {
    display: none;
  }

  & p {
    display: inline-block;
  }

  & .MuiTimelineConnector-root {
    opacity: 0.4;
    margin-top: 12px;
  }

  & .MuiChip-root {
    margin: ${({ theme }) => theme.spacing(0.25, 1)};
    font-size: 1rem;
  }

  & .MuiTimelineSeparator-root {
    margin-top: 12px;
  }
`;

const StyledTimelineSeparator = styled(TimelineSeparator)`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const HistoryItemWrapper = styled.div`
  background: ${({ theme }) => {
    if (theme.palette.mode === 'dark') return lighten(theme.palette.background.default, 0.14);
    return darken(theme.palette.background.default, 0.05);
  }};
  margin-top: ${({ theme }) => theme.spacing(0.5)};
  display: flex;
  align-items: center;
  min-height: 4.25rem;
  padding: ${({ theme }) => theme.spacing(1)};
  position: relative;

  &::before {
    content: '';
    border-style: solid;
    border-width: ${({ theme }) => theme.spacing(1, 1, 1, 0)};
    border-color: ${({ theme }) => {
      if (theme.palette.mode === 'dark') return `transparent ${lighten(theme.palette.background.default, 0.14)} transparent transparent`;
      return `transparent ${darken(theme.palette.background.default, 0.05)} transparent transparent`;
    }};
    position: absolute;
    left: ${({ theme }) => theme.spacing(-1)};
  }

  & .MuiTypography-subtitle2 {
    display: inline-flex;
    font-size: 1rem;
  }

  & .MuiChip-colorDefault {
    background: ${({ theme }) => theme.palette.mode === 'light' && theme.palette.getContrastText(theme.palette.text.primary)};
  }

  & .MuiChip-root.MuiChip-filledInfo {
    background-color: ${({ theme }) => (theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.background.paper)};
    border: 1.5px solid ${({ theme }) => (theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.background.paper)};

    & > .MuiChip-label {
      color: ${({ theme }) => theme.palette.text.primary};
    }
  }

  & .MuiChip-root {
    font-size: 0.9rem;
  }
`;

const PriorityChip = styled(Chip)`
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  width: 2.25rem;
  height: 2.25rem;

  & .MuiChip-label {
    font-size: 1rem;
    padding: 0;
  }
`;

const NoteWrapper = styled(Box)`
  background: ${({ theme }) => {
    if (theme.palette.mode === 'dark') return darken(theme.palette.background.default, 0.08);
    return lighten(theme.palette.background.default, 0.5);
  }};
  padding: ${({ theme }) => theme.spacing(1, 1.5)};
  display: flex;
  height: fit-content;
  margin-top: ${({ theme }) => theme.spacing(1)};
  margin-bottom: ${({ theme }) => theme.spacing(5)};
  border-radius: ${({ theme }) => theme.shape.borderRadius};
  min-height: 4.25rem;
  justify-content: flex-start;
  position: relative;

  & > strong {
    margin-right: ${({ theme }) => theme.spacing(1)};
    opacity: 0.7;
    width: fit-content;
    white-space: nowrap;
  }
`;

const Note = styled.p`
  margin: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(1.5)} ${theme.spacing(1)} ${theme.spacing(1.5)}`};
`;

const NoteLabel = styled.span`
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  margin-right: ${({ theme }) => theme.spacing(1)};
`;
const UpdateWord = styled.strong`
  margin-right: ${({ theme }) => theme.spacing(0.5)};
  white-space: nowrap;
`;

const StatusWord = styled.strong<{ noMargin?: number }>`
  margin-right: ${({ theme }) => theme.spacing(0.5)};
  margin-left: ${({ theme, noMargin }) => theme.spacing(noMargin ? 0 : 1.5)};
  white-space: nowrap;
`;

const Caption = styled.p`
  font-size: 0.85em;
  opacity: 0.4;
`;

const Content = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  height: 100%;
  column-gap: 0.35rem;
`;
